<template>
  <div class="rich-text-editor-wrapper">
    <label v-if="visiblelabel">{{ label }} <span class="red" v-if="requiredIcon">*</span></label>
    <div 
      ref="editorContainer" 
      :id="editorId"
      :class="{ 'p-invalid': errorMessages.length, 'editor-disabled': disabled }"
      class="rich-text-editor"
    ></div>
    <span v-if="errorMessages.length" class="err_msg_style">{{
      errorMessages[0]
    }}</span>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  onMounted,
  computed,
  watch,
  onBeforeUnmount,
  nextTick
} from "vue";
import { useField, validate } from "vee-validate";
import Quill from "quill";
import "quill/dist/quill.snow.css";

export default defineComponent({
  props: {
    value: {},
    input: {
      type: String,
      default: "",
    },
    id: {
      type: String,
      default: "",
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
    },
    placeholder: {
      type: String,
      default: "Enter text here...",
    },
    rules: {
      type: Object,
      default: null,
    },
    modelValue: {
      type: String,
      default: "",
    },
    visiblelabel: {
      type: Boolean,
      default: true
    },
    rows: {
      type: Number,
      default: 5,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    requiredIcon: {
      type: Boolean,
      default: false,
    },
    toolbar: {
      type: Array,
      default: () => [
        ['bold', 'italic', 'underline', 'strike'],
        ['blockquote', 'code-block'],
        [{ 'header': 1 }, { 'header': 2 }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'direction': 'rtl' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'font': [] }],
        [{ 'align': [] }],
        ['clean'],
        ['link', 'image']
      ]
    },
    height: {
      type: Number,
      default: 200
    }
  },

  setup(props, { emit }) {
    const editorContainer = ref<HTMLElement>();
    const quillInstance = ref<Quill>();
    const editorId = ref(`editor-${Math.random().toString(36).substr(2, 9)}`);
    const isUpdatingFromProp = ref(false);

    const { value, errorMessage } = useField(
      props.name,
      props.rules,
      { initialValue: props.modelValue }
    );

    const errorMessages = computed(() => {
      return errorMessage.value ? [errorMessage.value] : [];
    });

    const initializeQuill = async () => {
      await nextTick();
      
      if (!editorContainer.value) return;

      const options = {
        theme: 'snow',
        placeholder: props.placeholder,
        readOnly: props.disabled,
        modules: {
          toolbar: props.toolbar
        }
      };

      quillInstance.value = new Quill(editorContainer.value, options);

      // Set initial content
      if (props.modelValue) {
        quillInstance.value.root.innerHTML = props.modelValue;
      }

      // Set editor height
      if (props.height) {
        quillInstance.value.root.style.minHeight = `${props.height}px`;
      }

      // Listen for text changes
      quillInstance.value.on('text-change', (delta, oldDelta, source) => {
        if (isUpdatingFromProp.value) return;
        
        const html = quillInstance.value?.root.innerHTML || '';
        const text = quillInstance.value?.getText() || '';
        const isEmpty = text.trim().length === 0;
        const content = isEmpty ? '' : html;
        
        emit('update:modelValue', content);
        
        // Emit text-change event for variable mapping
        emit('text-change', { html: content, text: text, delta, oldDelta, source });
        
        // Validate the content
        validate(props.name, content).then((result) => {
          errorMessage.value = result.errors.length > 0 ? result.errors[0] : "";
        });
      });

      // Handle selection changes
      quillInstance.value.on('selection-change', (range, oldRange, source) => {
        if (!range) {
          emit('blur');
        }
        // Emit selection-change event for variable mapping
        emit('selection-change', { range, oldRange, source });
      });

      // Handle keyboard events for @ symbol detection
      quillInstance.value.keyboard.addBinding({
        key: '@',
        handler: function(range, context) {
          // Let the default behavior happen first
          setTimeout(() => {
            const text = quillInstance.value?.getText() || '';
            const html = quillInstance.value?.root.innerHTML || '';
            emit('update:modelValue', html);
            emit('text-change', { html, text, range, context });
          }, 0);
          return true; // Allow default behavior
        }
      });
    };

    // Watch for prop changes
    watch(() => props.modelValue, (newValue) => {
      if (quillInstance.value && !isUpdatingFromProp.value) {
        isUpdatingFromProp.value = true;
        const currentContent = quillInstance.value.root.innerHTML;
        if (currentContent !== newValue) {
          quillInstance.value.root.innerHTML = newValue || '';
        }
        isUpdatingFromProp.value = false;
      }
    });

    // Watch for disabled state changes
    watch(() => props.disabled, (newValue) => {
      if (quillInstance.value) {
        quillInstance.value.enable(!newValue);
      }
    });

    onMounted(() => {
      initializeQuill();
    });

    onBeforeUnmount(() => {
      if (quillInstance.value) {
        quillInstance.value = undefined;
      }
    });

    const clear = () => {
      if (quillInstance.value) {
        quillInstance.value.setText('');
        emit('update:modelValue', '');
      }
    };

    const getContent = () => {
      return quillInstance.value?.root.innerHTML || '';
    };

    const setContent = (content: string) => {
      if (quillInstance.value) {
        quillInstance.value.root.innerHTML = content;
      }
    };

    return {
      editorContainer,
      editorId,
      errorMessages,
      clear,
      getContent,
      setContent
    };
  },
});
</script>

<style scoped>
.rich-text-editor-wrapper {
  width: 100%;
}

.rich-text-editor {
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: white;
}

.rich-text-editor.p-invalid {
  border-color: #e24c4c;
}

.rich-text-editor.editor-disabled {
  background-color: #f3f4f6;
  opacity: 0.6;
}

.err_msg_style {
  color: #e24c4c;
  font-size: 12px;
  margin-top: 4px;
  display: block;
}

.red {
  color: #e24c4c;
}

/* Quill editor customizations */
:deep(.ql-editor) {
  min-height: 150px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
}

:deep(.ql-toolbar) {
  border-top: 1px solid #d1d5db;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  border-bottom: none;
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
}

:deep(.ql-container) {
  border-bottom: 1px solid #d1d5db;
  border-left: 1px solid #d1d5db;
  border-right: 1px solid #d1d5db;
  border-top: none;
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

:deep(.ql-editor.ql-blank::before) {
  font-style: normal;
  color: #9ca3af;
}
</style>
